import { Component, Prop, State, h, Host, Element, Build } from '@stencil/core';

const COMPONENT_NAME = 'sn-long-content-type-3';

// The left column changes role at $breakpoint-desktop-md — see `@include desktop-lg` in
// index.scss: below it the items are an accordion (inline content, no right column) where
// collapsing the open item is the point; from it up they are tabs whose answer renders in the
// right-hand panel, so a tab must never deselect — that would leave the panel blank.
const ACCORDION_BREAKPOINT = 960;

/**
 * Tabbed how-to section for SignNow forms pages. Displays a centered section title
 * and subtitle above a two-column tab layout: the left column contains clickable
 * question tabs (accordion on mobile, tab buttons on desktop); the right column
 * renders the HTML answer of the currently active tab on desktop.
 * Uses a light neutral background. Content is sourced from the `howto` backend data block.
 *
 * Items are passed as slot children with data-question and data-answer attributes.
 *
 * @product signnow
 * @source howto
 * @version 3.0.0
 * @category SignNow
 * @slot heading - <me-heading variant="2" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading
 * @slot subtitle - <me-text tag="p"> - Section subtitle text below the heading
 * @slot items - {required}, <div data-question="..." data-answer="..."> - Tab items
 * @example
 *   <sn-long-content-type-3>
 *     <me-heading slot="heading" variant="2" tag="h2">How to create an eSignature</me-heading>
 *     <me-text slot="subtitle" tag="p">Speed up your document workflow.</me-text>
 *     <div slot="items" data-question="How to eSign online" data-answer="&lt;p&gt;Follow these steps to sign.&lt;/p&gt;"></div>
 *     <div slot="items" data-question="How to send for signature" data-answer="&lt;p&gt;Upload your document.&lt;/p&gt;"></div>
 *   </sn-long-content-type-3>
 */
@Component({
  tag: 'sn-long-content-type-3',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnLongContentType3 {
  @Element() el!: HTMLElement;

  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  @State() activeIndex: number | null = 0;

  private items: Array<{ question: string; answer: string }> = [];

  componentWillLoad() {
    const slottedChildren = Array.from(this.el.querySelectorAll('[slot="items"]'));
    this.items = slottedChildren.map(child => ({
      question: child.getAttribute('data-question') || '',
      answer: child.getAttribute('data-answer') || '',
    }));
  }

  /** True while the left column is rendered as a mobile accordion. */
  private isAccordion(): boolean {
    if (!Build.isBrowser || typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(`(max-width: ${ACCORDION_BREAKPOINT - 1}px)`).matches;
  }

  render() {
    const items = this.items;
    if (items.length === 0) return <Host data-color-mode={this.colorMode}></Host>;

    // The panel falls back to the first item so it is never blank — reachable when the viewport
    // is resized past the breakpoint while the accordion sits collapsed.
    const activeItem = items[this.activeIndex ?? 0];

    return (
      <Host data-color-mode={this.colorMode}>
        <me-section class={COMPONENT_NAME} yGap="5xl" yGapLg="6xl">
          <div class={`${COMPONENT_NAME}__header`}>
              <slot name="heading"></slot>
              <slot name="subtitle"></slot>
            </div>
          <me-row yGap="none" yGapLg="none">
            <me-column span="12" spanLg="5">
              <ul class={`${COMPONENT_NAME}__tabs`} role="tablist">
                {items.map((item, i) => {
                  const isActive = i === this.activeIndex;
                  return (
                    <li
                      key={i}
                      class={`${COMPONENT_NAME}__tab-wrapper` + (isActive ? ` ${COMPONENT_NAME}__tab-wrapper--active` : '')}
                      role="presentation"
                    >
                      <div class={`${COMPONENT_NAME}__tab-item`}>
                        <button
                          class={`${COMPONENT_NAME}__tab`}
                          role="tab"
                          aria-selected={isActive ? 'true' : 'false'}
                          aria-expanded={isActive ? 'true' : 'false'}
                          onClick={() => { this.activeIndex = isActive && this.isAccordion() ? null : i; }}
                        >
                          <span class={`${COMPONENT_NAME}__tab-label`}>{item.question}</span>
                          <span class={`${COMPONENT_NAME}__tab-icon`}>
                            <me-icon name="arrows--arrow-down" color="currentColor" />
                          </span>
                        </button>
                      </div>
                      {/* Mobile accordion content — hidden on desktop via CSS */}
                      <div class={`${COMPONENT_NAME}__tab-content`}>
                        <div class={`${COMPONENT_NAME}__tab-content-inner`}>
                          <h3 class={`${COMPONENT_NAME}__panel-heading`}>{item.question}</h3>
                          <div class={`${COMPONENT_NAME}__panel-body`} innerHTML={item.answer}></div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </me-column>
            <me-column class={`${COMPONENT_NAME}__right-column`}>
              {/* Desktop-only content panel */}
              <div class={`${COMPONENT_NAME}__panel`} role="tabpanel">
                {activeItem && (
                  <div>
                    <h3 class={`${COMPONENT_NAME}__panel-heading`}>{activeItem.question}</h3>
                    <div class={`${COMPONENT_NAME}__panel-body`} innerHTML={activeItem.answer}></div>
                  </div>
                )}
              </div>
            </me-column>
          </me-row>
        </me-section>
      </Host>
    );
  }
}
